import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { formatApiError } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { Youtube, PlusCircle, LinkIcon, ExternalLink, Clock, CheckCircle2, XCircle, AlertCircle, Unlink, Info, Mail } from "lucide-react";

const STATUS_META = {
  pending:          { label: "Pending",         color: "bg-zinc-500/10 text-zinc-300 border-zinc-500/30",   icon: Clock },
  in_review:        { label: "In Review",       color: "bg-violet-500/10 text-violet-400 border-violet-500/30", icon: Clock },
  action_required:  { label: "Action Required", color: "bg-amber-500/10 text-amber-400 border-amber-500/30", icon: AlertCircle },
  linked:           { label: "Linked",          color: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30", icon: CheckCircle2 },
  created:          { label: "Created",         color: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30", icon: CheckCircle2 },
  rejected:         { label: "Rejected",        color: "bg-rose-500/10 text-rose-400 border-rose-500/30",   icon: XCircle },
  unlink_requested: { label: "Unlink Requested",color: "bg-amber-500/10 text-amber-400 border-amber-500/30", icon: Unlink },
  unlinked:         { label: "Unlinked",        color: "bg-zinc-500/10 text-zinc-400 border-zinc-500/30",   icon: Unlink },
};

const fmt = (iso) => iso ? new Date(iso).toLocaleString() : "—";

export default function UserYoutubeChannels() {
  const [rows, setRows] = useState([]);
  const [openLink, setOpenLink] = useState(false);
  const [openNew, setOpenNew] = useState(false);
  const [selected, setSelected] = useState(null);
  const [submitted, setSubmitted] = useState(null); // {request_code, id, request_type}

  const load = () => api.get("/yt-channels").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const linked = useMemo(() => rows.filter(r => r.status === "linked" || r.status === "created"), [rows]);
  const pending = useMemo(() => rows.filter(r => !["linked","created","rejected","unlinked"].includes(r.status)), [rows]);
  const actionReq = useMemo(() => rows.filter(r => r.status === "action_required"), [rows]);

  // Dismissible per-request approval banners (localStorage key)
  const dismissedKey = "sd_yt_ack";
  const [dismissed, setDismissed] = useState(() => { try { return JSON.parse(localStorage.getItem(dismissedKey) || "[]"); } catch { return []; } });
  const ackBanner = (id) => {
    const next = Array.from(new Set([...dismissed, id]));
    setDismissed(next); localStorage.setItem(dismissedKey, JSON.stringify(next));
  };
  const bannerFor = linked.find(r => !dismissed.includes(r.id));

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <div className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-400">Your Distribution</div>
      <h1 className="mt-1 text-4xl font-bold flex items-center gap-3"><Youtube className="w-8 h-8 text-rose-400" /> YouTube Channels</h1>
      <p className="mt-2 text-sm text-zinc-500 max-w-2xl">Link an existing YouTube channel or request SonotekDigi to create a new one for you. Every request is tracked with an ID and admin approval.</p>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat k="Total Channels" v={linked.length} testid="yt-stat-total" />
        <Stat k="Linked / Created" v={linked.length} tone="emerald" testid="yt-stat-linked" />
        <Stat k="Pending" v={pending.length} tone="violet" testid="yt-stat-pending" />
        <Stat k="Action Required" v={actionReq.length} tone="amber" testid="yt-stat-action" />
      </div>

      {/* Dashboard banner — post-approval email-check reminder */}
      {bannerFor && (
        <div className="mt-6 rounded-2xl border border-emerald-500/40 bg-emerald-500/5 p-4 flex items-start gap-3" data-testid="yt-approved-banner">
          <Mail className="w-5 h-5 text-emerald-400 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-emerald-300">Action Required: Check Your Email</div>
            <div className="text-sm text-zinc-300 mt-1">
              Your YouTube channel request <span className="font-mono text-emerald-400">{bannerFor.request_code}</span> has been processed.
              Please check your email for any invitation or confirmation from YouTube and accept it if required.
            </div>
          </div>
          <div className="flex flex-col md:flex-row gap-2">
            <Button size="sm" onClick={() => setSelected(bannerFor)} data-testid="yt-banner-view"
              className="bg-emerald-500 text-zinc-950 hover:bg-emerald-600 font-semibold">View Channel Details</Button>
            <Button size="sm" variant="ghost" onClick={() => ackBanner(bannerFor.id)} data-testid="yt-banner-dismiss"
              className="text-zinc-400 hover:text-zinc-100">Dismiss</Button>
          </div>
        </div>
      )}

      {/* Permanent processing-time notice */}
      <div className="mt-6 rounded-2xl border border-cyan-500/30 bg-cyan-500/5 p-4 flex items-start gap-3" data-testid="yt-processing-notice">
        <Info className="w-5 h-5 text-cyan-400 mt-0.5" />
        <div>
          <div className="font-semibold text-cyan-300">Processing Time</div>
          <div className="text-sm text-zinc-300 mt-1">
            YouTube channel linking and new channel creation requests usually take approximately <b>4–5 days</b> to process.
            You can track the latest status of your request here.
          </div>
        </div>
      </div>

      <div className="mt-6 grid md:grid-cols-2 gap-4">
        <button onClick={() => setOpenLink(true)} data-testid="yt-open-link-existing"
          className="text-left rounded-2xl border border-cyan-500/30 bg-gradient-to-br from-cyan-500/10 to-transparent p-6 hover:brightness-110 transition">
          <LinkIcon className="w-6 h-6 text-cyan-400 mb-3" />
          <div className="text-lg font-semibold">Link Existing Channel</div>
          <div className="text-sm text-zinc-400 mt-1">Already have a YouTube channel? Send it for admin verification.</div>
        </button>
        <button onClick={() => setOpenNew(true)} data-testid="yt-open-request-new"
          className="text-left rounded-2xl border border-violet-500/30 bg-gradient-to-br from-violet-500/10 to-transparent p-6 hover:brightness-110 transition">
          <PlusCircle className="w-6 h-6 text-violet-400 mb-3" />
          <div className="text-lg font-semibold">Request New Channel</div>
          <div className="text-sm text-zinc-400 mt-1">Ask SonotekDigi to create a fresh YouTube channel for you.</div>
        </button>
      </div>

      {/* Linked channels */}
      <div className="mt-10">
        <h2 className="text-lg font-semibold mb-3">My YouTube Channels</h2>
        {linked.length === 0 ? (
          <div className="p-8 text-center text-zinc-500 rounded-2xl border border-zinc-800 bg-zinc-950/60">No linked channels yet.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {linked.map(r => (
              <div key={r.id} data-testid={`yt-channel-${r.id}`} className="p-4 rounded-2xl border border-zinc-800 bg-zinc-950/60">
                <div className="flex items-center gap-2 text-xs text-zinc-500 font-mono">{r.request_code}</div>
                <div className="text-lg font-semibold mt-1">{r.channel_name}</div>
                <div className="text-xs text-zinc-500 font-mono mt-1">ID: {r.channel_id || "—"}</div>
                <div className="text-xs text-zinc-500 mt-0.5">{r.status === "linked" ? "Linked" : "Created"}: {fmt(r.linked_at || r.created_channel_at)}</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <a href={r.channel_url} target="_blank" rel="noreferrer" data-testid={`yt-open-${r.id}`}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-rose-500/40 text-rose-400 hover:bg-rose-500/10">
                    <ExternalLink className="w-3 h-3" /> Open YouTube Channel
                  </a>
                  <Button size="sm" variant="ghost" onClick={() => setSelected(r)} className="text-cyan-400 h-auto py-1 px-3 text-xs"
                    data-testid={`yt-detail-${r.id}`}>Details</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Request history */}
      <div className="mt-10">
        <h2 className="text-lg font-semibold mb-3">Request History</h2>
        <div className="rounded-2xl border border-zinc-800 bg-zinc-950/60 overflow-x-auto">
          <table className="w-full min-w-[700px] text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                <th className="p-3">Request ID</th><th className="p-3">Type</th>
                <th className="p-3">Channel</th><th className="p-3">Status</th>
                <th className="p-3">Submitted</th><th className="p-3">Updated</th><th className="p-3"></th>
              </tr>
            </thead>
            <tbody data-testid="yt-request-tbody">
              {rows.length === 0 && <tr><td colSpan="7" className="p-8 text-center text-zinc-500">No requests yet.</td></tr>}
              {rows.map(r => {
                const meta = STATUS_META[r.status] || STATUS_META.pending;
                const Icon = meta.icon;
                return (
                  <tr key={r.id} data-testid={`yt-req-${r.id}`} className="border-b border-zinc-900 hover:bg-zinc-900/40 cursor-pointer" onClick={() => setSelected(r)}>
                    <td className="p-3 font-mono text-xs">{r.request_code}</td>
                    <td className="p-3 text-xs">{r.request_type === "existing" ? "Existing" : "New"}</td>
                    <td className="p-3">{r.channel_name || r.desired_channel_name || "—"}</td>
                    <td className="p-3"><span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs border ${meta.color}`}><Icon className="w-3 h-3" />{meta.label}</span></td>
                    <td className="p-3 text-xs text-zinc-500">{fmt(r.created_at)}</td>
                    <td className="p-3 text-xs text-zinc-500">{fmt(r.updated_at)}</td>
                    <td className="p-3 text-right">
                      <Button size="sm" variant="ghost" className="text-cyan-400 text-xs">View</Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <LinkExistingDialog open={openLink} onClose={() => { setOpenLink(false); load(); }}
        onSubmitted={(data) => setSubmitted({ ...data, request_type: "existing" })} />
      <RequestNewDialog open={openNew} onClose={() => { setOpenNew(false); load(); }}
        onSubmitted={(data) => setSubmitted({ ...data, request_type: "new" })} />
      <SubmittedDialog data={submitted} onClose={() => setSubmitted(null)} onView={(rid) => {
        setSubmitted(null);
        const found = rows.find(r => r.id === rid);
        if (found) setSelected(found);
      }} />
      <RequestDetail req={selected} onClose={() => { setSelected(null); load(); }} />
    </div>
  );
}

function SubmittedDialog({ data, onClose, onView }) {
  return (
    <Dialog open={!!data} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-emerald-500/30 max-w-md" data-testid="yt-submitted-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <DialogTitle>Request Submitted Successfully</DialogTitle>
              <DialogDescription className="text-zinc-500">
                Request ID <span className="font-mono text-emerald-400">{data?.request_code}</span>
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <div className="text-sm text-zinc-300 leading-relaxed">
          Your YouTube channel request has been submitted successfully. Our team will review and process your request.
          Please allow approximately <b>4–5 days</b> for processing. You can track the status of your request from the
          <b> YouTube Channels</b> section of your dashboard.
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="border-zinc-700 hover:bg-zinc-900">Close</Button>
          <Button onClick={() => onView(data?.id)} data-testid="yt-submitted-view"
            className="bg-emerald-500 text-zinc-950 hover:bg-emerald-600 font-semibold">View My Request</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ k, v, tone, testid }) {
  const c = tone === "emerald" ? "text-emerald-400" : tone === "violet" ? "text-violet-400" : tone === "amber" ? "text-amber-400" : "text-zinc-100";
  return <div className="p-4 rounded-xl border border-zinc-800 bg-zinc-950/60"><div className="text-[10px] uppercase tracking-wider text-zinc-500">{k}</div><div className={`mt-1.5 text-2xl font-semibold ${c}`} data-testid={testid}>{v}</div></div>;
}

function LinkExistingDialog({ open, onClose, onSubmitted }) {
  const [f, setF] = useState({ channel_name: "", channel_url: "", user_notes: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ channel_name: "", channel_url: "", user_notes: "" }); }, [open]);
  const submit = async () => {
    if (!f.channel_name || !f.channel_url) return toast.error("Channel Name & URL required");
    setBusy(true);
    try {
      const { data } = await api.post("/yt-channels/link-existing", f);
      onSubmitted && onSubmitted(data);
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Submit failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md" data-testid="yt-link-dialog">
        <DialogHeader><DialogTitle>Link Existing Channel</DialogTitle>
          <DialogDescription className="text-zinc-500">We'll send this to admin for verification.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <Field label="Channel Name *"><Input value={f.channel_name} onChange={(e) => setF({...f, channel_name: e.target.value})} data-testid="yt-link-name" className="bg-zinc-900 border-zinc-800" /></Field>
          <Field label="YouTube Channel URL *"><Input value={f.channel_url} onChange={(e) => setF({...f, channel_url: e.target.value})} data-testid="yt-link-url" placeholder="https://youtube.com/@yourchannel" className="bg-zinc-900 border-zinc-800" /></Field>
          <Field label="Notes (optional)"><Textarea value={f.user_notes} onChange={(e) => setF({...f, user_notes: e.target.value})} data-testid="yt-link-notes" className="bg-zinc-900 border-zinc-800" /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-zinc-700 hover:bg-zinc-900">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="yt-link-submit" className="bg-gradient-to-r from-cyan-400 to-violet-500 text-zinc-950 font-semibold">Submit Link Request</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RequestNewDialog({ open, onClose, onSubmitted }) {
  const [f, setF] = useState({ desired_channel_name: "", email: "", user_notes: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ desired_channel_name: "", email: "", user_notes: "" }); }, [open]);
  const submit = async () => {
    if (!f.desired_channel_name || !f.email) return toast.error("Desired Channel Name & Email required");
    setBusy(true);
    try {
      const { data } = await api.post("/yt-channels/request-new", f);
      onSubmitted && onSubmitted(data);
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Submit failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md" data-testid="yt-new-dialog">
        <DialogHeader><DialogTitle>Request New Channel</DialogTitle>
          <DialogDescription className="text-zinc-500">Admin will create the channel and share the details with you.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <Field label="Desired Channel Name *"><Input value={f.desired_channel_name} onChange={(e) => setF({...f, desired_channel_name: e.target.value})} data-testid="yt-new-name" className="bg-zinc-900 border-zinc-800" /></Field>
          <Field label="Email ID *"><Input type="email" value={f.email} onChange={(e) => setF({...f, email: e.target.value})} data-testid="yt-new-email" className="bg-zinc-900 border-zinc-800" /></Field>
          <Field label="Notes (optional)"><Textarea value={f.user_notes} onChange={(e) => setF({...f, user_notes: e.target.value})} data-testid="yt-new-notes" className="bg-zinc-900 border-zinc-800" /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-zinc-700 hover:bg-zinc-900">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="yt-new-submit" className="bg-gradient-to-r from-cyan-400 to-violet-500 text-zinc-950 font-semibold">Submit New Channel Request</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return <div><Label className="text-xs uppercase tracking-wider text-zinc-400">{label}</Label><div className="mt-1.5">{children}</div></div>;
}

function RequestDetail({ req, onClose }) {
  const [respOpen, setRespOpen] = useState(false);
  const [unlinkOpen, setUnlinkOpen] = useState(false);
  const [respForm, setRespForm] = useState({ channel_url: "", channel_id: "", user_notes: "" });
  const [unlinkReason, setUnlinkReason] = useState("");
  const meta = req ? (STATUS_META[req.status] || STATUS_META.pending) : null;
  const submitResp = async () => {
    try { await api.post(`/yt-channels/${req.id}/respond`, respForm); toast.success("Response sent"); setRespOpen(false); onClose(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  const submitUnlink = async () => {
    if (!unlinkReason.trim()) return toast.error("Reason required");
    try { await api.post(`/yt-channels/${req.id}/unlink-request`, { reason: unlinkReason }); toast.success("Unlink request sent"); setUnlinkOpen(false); onClose(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  return (
    <Dialog open={!!req} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-2xl" data-testid="yt-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">{req?.request_code}
            {meta && <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs border ${meta.color}`}>{meta.label}</span>}</DialogTitle>
        </DialogHeader>
        {req && (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {(req.status === "pending" || req.status === "in_review") && (
              <div className="p-3 rounded-lg border border-cyan-500/30 bg-cyan-500/5 text-xs flex items-center gap-2" data-testid="yt-est-time">
                <Info className="w-4 h-4 text-cyan-400" />
                <span className="text-zinc-300"><b>Estimated Processing Time:</b> 4–5 days</span>
              </div>
            )}
            {(req.status === "linked" || req.status === "created") && (
              <div className="p-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5 text-sm" data-testid="yt-post-approval-note">
                <div className="text-emerald-300 font-semibold mb-1">
                  {req.status === "linked" ? "Your YouTube Channel Has Been Linked" : "Your YouTube Channel Is Ready"}
                </div>
                <div className="text-zinc-300">
                  Your {req.status === "linked" ? "channel linking" : "new YouTube channel"} request has been processed.
                  Please check the email {req.status === "linked" ? "account associated with your YouTube channel" : "address provided in your request"}.
                  You may receive an invitation or confirmation email from YouTube. If an invitation is available,
                  please open the email and accept it to complete the process.
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2 text-sm">
              <M k="Type" v={req.request_type === "existing" ? "Existing Channel" : "New Channel"} />
              <M k="Channel Name" v={req.channel_name} />
              <M k="Channel URL" v={req.channel_url} />
              <M k="Channel ID" v={req.channel_id} mono />
              <M k="Email" v={req.email} />
              <M k="Submitted" v={fmt(req.created_at)} />
            </div>
            {req.status === "action_required" && req.action_required_message && (
              <div className="p-3 rounded-lg border border-amber-500/30 bg-amber-500/5 text-sm">
                <div className="text-amber-400 text-xs uppercase tracking-wider mb-1">Action required from you</div>
                {req.action_required_message}
              </div>
            )}
            {req.admin_note_public && (
              <div className="p-3 rounded-lg border border-zinc-800 bg-zinc-900 text-sm">
                <div className="text-cyan-400 text-xs uppercase tracking-wider mb-1">Admin note</div>{req.admin_note_public}
              </div>
            )}
            {req.user_notes && (
              <div className="p-3 rounded-lg border border-zinc-800 bg-zinc-900 text-sm">
                <div className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Your notes</div>{req.user_notes}
              </div>
            )}
            <div>
              <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Activity</div>
              <div className="space-y-1.5">
                {(req.history || []).map((h, i) => (
                  <div key={i} className="text-xs text-zinc-400 flex gap-2">
                    <span className="font-mono text-zinc-500">{fmt(h.at)}</span>
                    <span className="text-zinc-300">{h.action}</span>
                    {h.new_status && <span className="text-emerald-400">→ {h.new_status}</span>}
                    {h.detail && <span className="text-zinc-500">· {h.detail}</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        <DialogFooter className="gap-2 flex-wrap">
          <Button variant="outline" onClick={onClose} className="border-zinc-700 hover:bg-zinc-900">Close</Button>
          {req && (req.status === "action_required" || req.status === "in_review" || req.status === "pending") && (
            <Button onClick={() => setRespOpen(true)} data-testid="yt-respond-open" className="bg-amber-500 text-zinc-950 hover:bg-amber-600 font-semibold">Provide info</Button>
          )}
          {req && (req.status === "linked" || req.status === "created") && (
            <Button onClick={() => setUnlinkOpen(true)} data-testid="yt-unlink-open" className="bg-rose-500 text-zinc-950 hover:bg-rose-600 font-semibold"><Unlink className="w-4 h-4 mr-1.5" /> Request Unlink</Button>
          )}
        </DialogFooter>
      </DialogContent>

      <Dialog open={respOpen} onOpenChange={setRespOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md">
          <DialogHeader><DialogTitle>Update your request</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Channel URL"><Input value={respForm.channel_url} onChange={(e) => setRespForm({...respForm, channel_url: e.target.value})} data-testid="yt-resp-url" className="bg-zinc-900 border-zinc-800" /></Field>
            <Field label="Channel ID"><Input value={respForm.channel_id} onChange={(e) => setRespForm({...respForm, channel_id: e.target.value})} data-testid="yt-resp-cid" className="bg-zinc-900 border-zinc-800" /></Field>
            <Field label="Notes"><Textarea value={respForm.user_notes} onChange={(e) => setRespForm({...respForm, user_notes: e.target.value})} data-testid="yt-resp-notes" className="bg-zinc-900 border-zinc-800" /></Field>
          </div>
          <DialogFooter><Button onClick={submitResp} data-testid="yt-resp-submit" className="bg-cyan-500 text-zinc-950 hover:bg-cyan-600">Send</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={unlinkOpen} onOpenChange={setUnlinkOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md">
          <DialogHeader><DialogTitle>Request Unlink</DialogTitle>
            <DialogDescription className="text-zinc-500">The channel stays linked until an admin approves.</DialogDescription></DialogHeader>
          <Field label="Reason *"><Textarea value={unlinkReason} onChange={(e) => setUnlinkReason(e.target.value)} data-testid="yt-unlink-reason" className="bg-zinc-900 border-zinc-800" /></Field>
          <DialogFooter><Button onClick={submitUnlink} data-testid="yt-unlink-submit" className="bg-rose-500 text-zinc-950 hover:bg-rose-600">Send Request</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

function M({ k, v, mono }) {
  return <div className="p-2.5 rounded-lg bg-zinc-900/60 border border-zinc-800"><div className="text-[10px] uppercase tracking-wider text-zinc-500">{k}</div><div className={`mt-0.5 ${mono ? "font-mono text-xs" : "text-sm"}`}>{v || "—"}</div></div>;
}
